import { useState, useCallback, useEffect, useRef } from 'react';
import { X, Search, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface Contact {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

interface ContactCellProps {
  value: Contact | Contact[] | null;
  contacts: Contact[]; // Available contacts to select from
  onChange: (value: Contact | Contact[] | null) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  allowMultiple?: boolean;
  className?: string;
}

export function ContactCell({
  value,
  contacts,
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  allowMultiple = false,
  className,
}: ContactCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalize value to array
  const selectedContacts: Contact[] = Array.isArray(value)
    ? value
    : value
      ? [value]
      : [];

  const selectedIds = selectedContacts.map((c) => c.id);

  useEffect(() => {
    if (isEditing) {
      setIsOpen(true);
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setIsOpen(false);
    }
  }, [isEditing]);

  const handleSelect = useCallback(
    (contact: Contact) => {
      if (allowMultiple) {
        const isSelected = selectedIds.includes(contact.id);
        const newContacts = isSelected
          ? selectedContacts.filter((c) => c.id !== contact.id)
          : [...selectedContacts, contact];
        onChange(newContacts.length > 0 ? newContacts : null);
      } else {
        onChange(contact);
        setIsOpen(false);
        onEndEdit();
      }
    },
    [allowMultiple, selectedContacts, selectedIds, onChange, onEndEdit]
  );

  const handleRemove = useCallback(
    (contactId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const newContacts = selectedContacts.filter((c) => c.id !== contactId);
      onChange(
        allowMultiple
          ? newContacts.length > 0
            ? newContacts
            : null
          : null
      );
    },
    [selectedContacts, onChange, allowMultiple]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        onEndEdit();
      }
    },
    [onEndEdit]
  );

  const filteredContacts = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderContactChip = (contact: Contact, showRemove = false) => (
    <div
      key={contact.id}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded-full text-xs"
    >
      <Avatar className="h-4 w-4">
        <AvatarImage src={contact.avatarUrl} alt={contact.name} />
        <AvatarFallback className="text-[8px]">
          {getInitials(contact.name)}
        </AvatarFallback>
      </Avatar>
      <span className="max-w-[80px] truncate">{contact.name}</span>
      {showRemove && (
        <button
          className="hover:bg-gray-200 rounded-full p-0.5"
          onClick={(e) => handleRemove(contact.id, e)}
        >
          <X className="h-3 w-3 text-gray-500" />
        </button>
      )}
    </div>
  );

  const displayContent = () => {
    if (selectedContacts.length === 0) {
      return (
        <span className="text-gray-400 flex items-center gap-1">
          <UserPlus className="h-3.5 w-3.5" />
          Assign...
        </span>
      );
    }

    if (!allowMultiple && selectedContacts.length === 1) {
      const contact = selectedContacts[0];
      return (
        <div className="flex items-center gap-2">
          <Avatar className="h-5 w-5">
            <AvatarImage src={contact.avatarUrl} alt={contact.name} />
            <AvatarFallback className="text-[10px]">
              {getInitials(contact.name)}
            </AvatarFallback>
          </Avatar>
          <span className="truncate">{contact.name}</span>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-1">
        {selectedContacts.slice(0, 2).map((c) => renderContactChip(c, true))}
        {selectedContacts.length > 2 && (
          <span className="text-xs text-gray-500 py-0.5">
            +{selectedContacts.length - 2}
          </span>
        )}
      </div>
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'w-full h-full px-2 py-1 text-sm flex items-center cursor-pointer',
            'hover:bg-gray-50',
            isEditing && 'bg-white ring-2 ring-blue-500',
            className
          )}
          onClick={onStartEdit}
        >
          {displayContent()}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-2 py-1.5 text-sm border rounded outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {selectedContacts.length > 0 && allowMultiple && (
          <div className="p-2 border-b bg-gray-50">
            <div className="text-xs text-gray-500 mb-1">Selected:</div>
            <div className="flex flex-wrap gap-1">
              {selectedContacts.map((c) => renderContactChip(c, true))}
            </div>
          </div>
        )}

        <div className="max-h-60 overflow-auto">
          {filteredContacts.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              No contacts found
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-100',
                  selectedIds.includes(contact.id) && 'bg-blue-50'
                )}
                onClick={() => handleSelect(contact)}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={contact.avatarUrl} alt={contact.name} />
                  <AvatarFallback>{getInitials(contact.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {contact.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {contact.email}
                  </div>
                </div>
                {selectedIds.includes(contact.id) && (
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                )}
              </div>
            ))
          )}
        </div>

        {allowMultiple && (
          <div className="p-2 border-t flex justify-end">
            <button
              className="text-sm text-blue-500 hover:text-blue-700"
              onClick={() => {
                setIsOpen(false);
                onEndEdit();
              }}
            >
              Done
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default ContactCell;
